import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { addActivity } from '../utils/cardUtils';

import {
    ModalOverlay,
    ModalContent,
    ModalHeader,
    SearchInput,
    CardListContainer,
    CardListItem,
    NoResultsMessage,
    ModalActions,
    CancelButton,
} from '../styles/AddBlockerModal.styles';

const AddBlockerModal = ({
    isOpen,
    onClose,
    projectId,
    currentCardId, // The card that will be blocked
    currentCardTitle,
    currentCardBlockedByIds = [], // Existing blockers for the current card
    allColumns,
    onBlockerAdded, // Callback: (updatedCurrentCard, updatedBlockerCard) => void
}) => {
    const { user } = useAuth();
    const [searchTerm, setSearchTerm] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (isOpen) {
            setSearchTerm('');
        }
    }, [isOpen]);

    const availableForBlocking = useMemo(() => {
        if (!allColumns || !currentCardId) return [];
        const allFlattenedCards = [];
        for (const columnId in allColumns) {
            if (allColumns[columnId] && allColumns[columnId].cards) {
                allFlattenedCards.push(...allColumns[columnId].cards);
            }
        }
        const blockedByIdsSet = new Set(currentCardBlockedByIds);
        return allFlattenedCards.filter(card => {
            if (!card || !card.id) return false;
            // Cannot block itself, and cannot add if already blocking
            return card.id !== currentCardId && !blockedByIdsSet.has(card.id);
        });
    }, [allColumns, currentCardId, currentCardBlockedByIds]);

    const filteredCandidates = useMemo(() => {
        if (!searchTerm) {
            return availableForBlocking;
        }
        return availableForBlocking.filter(card =>
            card.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [availableForBlocking, searchTerm]);

    const handleSelectBlocker = async (selectedBlockerCardInfo) => {
        if (!selectedBlockerCardInfo || isSaving) return;

        setIsSaving(true);
        const selectedBlockerCardId = selectedBlockerCardInfo.id;

        try {
            const projectRef = doc(db, 'projects', projectId);
            const projectSnap = await getDoc(projectRef);
            if (!projectSnap.exists()) throw new Error("Project not found");
            const projectData = projectSnap.data();

            // --- Find and prepare current card (the one being blocked) for update ---
            let currentCardToUpdate = null;
            let currentCardColId = null;
            for (const colId in projectData.columns) {
                const found = projectData.columns[colId].cards.find(c => c.id === currentCardId);
                if (found) {
                    currentCardToUpdate = found;
                    currentCardColId = colId;
                    break;
                }
            }
            if (!currentCardToUpdate) throw new Error("Current card (to be blocked) not found.");

            const newBlockedByForCurrent = [...(currentCardToUpdate.blockedByCardIds || []), selectedBlockerCardId];
            const activityDetailsCurrent = `marked as blocked by card "${selectedBlockerCardInfo.title}"`;
            const newActivityForCurrent = addActivity(user, { type: 'card_blocked_by', details: activityDetailsCurrent });
            const updatedCurrentCard = {
                ...currentCardToUpdate,
                blockedByCardIds: newBlockedByForCurrent,
                activity: [...(currentCardToUpdate.activity || []), newActivityForCurrent]
            };

            // --- Find and prepare the blocker card for activity update ---
            let blockerCardToUpdate = null;
            let blockerCardColId = null;
            for (const colId in projectData.columns) {
                const found = projectData.columns[colId].cards.find(c => c.id === selectedBlockerCardId);
                if (found) {
                    blockerCardToUpdate = found;
                    blockerCardColId = colId;
                    break;
                }
            }
            if (!blockerCardToUpdate) throw new Error("Blocker card not found for activity update.");

            const activityDetailsBlocker = `is now blocking card "${currentCardTitle}"`;
            const newActivityForBlocker = addActivity(user, { type: 'card_is_blocking', details: activityDetailsBlocker });
            const updatedBlockerCard = { // Only activity changes on the blocker card itself
                ...blockerCardToUpdate,
                activity: [...(blockerCardToUpdate.activity || []), newActivityForBlocker]
            };

            // --- Prepare Firestore updates ---
            const firestoreUpdates = { updatedAt: new Date().toISOString() };
            if (currentCardColId === blockerCardColId) { // Both cards in the same column
                const updatedCardsInSharedColumn = projectData.columns[currentCardColId].cards.map(c => {
                    if (c.id === currentCardId) return updatedCurrentCard;
                    if (c.id === selectedBlockerCardId) return updatedBlockerCard;
                    return c;
                });
                firestoreUpdates[`columns.${currentCardColId}.cards`] = updatedCardsInSharedColumn;
            } else { // Cards in different columns
                const updatedCardsInCurrentCol = projectData.columns[currentCardColId].cards.map(c =>
                    c.id === currentCardId ? updatedCurrentCard : c
                );
                firestoreUpdates[`columns.${currentCardColId}.cards`] = updatedCardsInCurrentCol;

                const updatedCardsInBlockerCol = projectData.columns[blockerCardColId].cards.map(c =>
                    c.id === selectedBlockerCardId ? updatedBlockerCard : c
                );
                firestoreUpdates[`columns.${blockerCardColId}.cards`] = updatedCardsInBlockerCol;
            }

            await updateDoc(projectRef, firestoreUpdates);

            if (onBlockerAdded) {
                onBlockerAdded(updatedCurrentCard, updatedBlockerCard);
            }
            onClose();
        } catch (error) {
            console.error('Error adding blocker:', error);
            alert('Failed to add blocker. ' + error.message);
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalOverlay onClick={onClose}>
            <ModalContent onClick={e => e.stopPropagation()}>
                <ModalHeader>Add Blocker Card</ModalHeader>
                <SearchInput
                    type="text"
                    placeholder="Search cards by title..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
                <CardListContainer>
                    {filteredCandidates.length > 0 ? (
                        filteredCandidates.map(card => (
                            <CardListItem
                                key={card.id}
                                onClick={() => handleSelectBlocker(card)}
                                title={card.title}
                                disabled={isSaving}
                            >
                                {card.title}
                            </CardListItem>
                        ))
                    ) : (
                        <NoResultsMessage>
                            {searchTerm ? "No matching cards found." : "No available cards to set as blocker."}
                        </NoResultsMessage>
                    )}
                </CardListContainer>
                <ModalActions>
                    <CancelButton onClick={onClose} disabled={isSaving}>Cancel</CancelButton>
                </ModalActions>
            </ModalContent>
        </ModalOverlay>
    );
};

export default AddBlockerModal;
